import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Modal,
  Select,
  Progress,
  Avatar,
  Badge,
  Space,
  List,
  Divider,
  Descriptions,
  Empty,
  notification,
  Spin,
  Typography,
  Drawer,
  Input,
  FloatButton,
  Grid,
  Statistic,
  Carousel,
  Steps,
  Tooltip,
  Collapse,
  Tabs,
  Timeline,
  Table,
  Alert
} from 'antd';
import {
  ReloadOutlined,
  SettingOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  DashboardOutlined,
  BellOutlined,
  CrownOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  CrownFilled,
  TrophyOutlined,
  SearchOutlined,
  CalendarOutlined,
  GatewayOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  RiseOutlined,
  FallOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MoreOutlined,
  ExperimentOutlined,
  FireOutlined,
  NodeIndexOutlined,
  ApartmentOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  SwapOutlined,
  MenuOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  UpOutlined,
  DownOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  HeatMapOutlined,
  BugOutlined,
  CodeOutlined,
  WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import API from '../../API/API';
import './RRVisualizationDashboard.css';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;
const { Panel } = Collapse;
const { TabPane } = Tabs;

// ==================== SMOOTH THEME ====================
const THEME = {
  primary: '#6366f1',
  primaryLight: '#818cf8',
  primaryDark: '#4f46e5',
  success: '#10b981',
  successLight: '#d1fae5',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  purple: '#8b5cf6',
  purpleLight: '#ede9fe',
  background: '#f9fafb',
  cardBg: '#ffffff',
  cardBgAlt: '#f3f4f6',
  border: '#e5e7eb',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
  textLight: '#9ca3af',
  shadow: '0 1px 3px rgba(0,0,0,0.05), 0 20px 25px -5px rgba(0,0,0,0.05)',
  shadowHover: '0 10px 40px -15px rgba(0,0,0,0.2)',
  gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  gradientSuccess: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
};

const TIER_STYLES = {
  'TIER-1': {
    primary: '#6366f1',
    light: '#e0e7ff',
    gradient: 'linear-gradient(145deg, #6366f1, #4f46e5)',
    icon: <CrownOutlined />,
    badge: 'Premium'
  },
  'TIER-2': {
    primary: '#10b981',
    light: '#d1fae5',
    gradient: 'linear-gradient(145deg, #10b981, #059669)',
    icon: <RocketOutlined />,
    badge: 'Business'
  },
  'TIER-3': {
    primary: '#f59e0b',
    light: '#fef3c7',
    gradient: 'linear-gradient(145deg, #f59e0b, #d97706)',
    icon: <FireOutlined />,
    badge: 'Standard'
  }
};

/**
 * The rings come from GET /rr-visualization/rings, which builds one per domain
 * group from the DomainGroup collection. Nothing here names a lane, so a group
 * an admin adds or renames in Domain Mapping appears in every selector below
 * without this file changing.
 */
const toRingOption = (ring) => ({
  value: ring.ringId,
  label: ring.label || ring.ringId
});

const getRingBaseTier = (item) => {
  const source = typeof item === 'string' ? item : item?.tier || item?.ringId || '';
  if (source.startsWith('TIER-1')) return 'TIER-1';
  if (source.startsWith('TIER-2')) return 'TIER-2';
  if (source.startsWith('TIER-3')) return 'TIER-3';
  return 'TIER-1';
};

const getRingStyle = (item) => TIER_STYLES[getRingBaseTier(item)] || TIER_STYLES['TIER-1'];

const getRingLabel = (item, ringOptions = []) => {
  if (!item) return 'Unknown Ring';
  if (typeof item === 'string') {
    return ringOptions.find((option) => option.value === item)?.label || item;
  }
  return item.label || item.ringLabel || item.ringId || item.tier || 'Unknown Ring';
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// ==================== MAIN DASHBOARD ====================
const RRVisualizationDashboard = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTier, setSelectedTier] = useState('ALL');
  const [timeRange, setTimeRange] = useState('7d');
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedTierForRotation, setSelectedTierForRotation] = useState('ALL');
  // Empty until the rings arrive; the effect below selects the first one.
  const [ringOptions, setRingOptions] = useState([]);
  const [selectedTierForHistory, setSelectedTierForHistory] = useState(null);
  const [selectedTierForSimulation, setSelectedTierForSimulation] = useState(null);
  const [selectedTierForSequence, setSelectedTierForSequence] = useState(null);
  const [selectedTierForTimeline, setSelectedTierForTimeline] = useState(null);
  const [selectedTierForDebug, setSelectedTierForDebug] = useState(null);

  // Data States
  const [cursors, setCursors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [fairnessData, setFairnessData] = useState([]);
  const [historyData, setHistoryData] = useState(null);
  const [simulationData, setSimulationData] = useState(null);
  const [sequenceData, setSequenceData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [debugData, setDebugData] = useState(null);
  const [compatibilityData, setCompatibilityData] = useState(null);
  const [exportFormat, setExportFormat] = useState('json');
  
  // UI
  const [activeAgent, setActiveAgent] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [rotationView, setRotationView] = useState('grid');
  const [activeTab, setActiveTab] = useState('overview');
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetRingId, setResetRingId] = useState(null);
  const [simulationSteps, setSimulationSteps] = useState(5);
  const [historyLimit, setHistoryLimit] = useState(50);
  const [exportParams, setExportParams] = useState({
    format: 'json',
    tier: 'ALL',
    startDate: null,
    endDate: null
  });
  const [apiError, setApiError] = useState(null);

  // ==================== DATA FETCHING ====================

  // Fetch the live rings, one per domain group, and point every ring selector
  // at the first of them. Runs once: the selectors keep whatever the user picks.
  const fetchRings = useCallback(async () => {
    try {
      const response = await API.fetchRRRings();

      if (!response?.success || !Array.isArray(response.data)) {
        console.error('Unexpected /rr-visualization/rings response:', response);
        return;
      }

      const options = response.data.map(toRingOption);
      setRingOptions(options);

      const firstRingId = options[0]?.value || null;
      if (!firstRingId) {
        return;
      }

      setSelectedTierForHistory((current) => current || firstRingId);
      setSelectedTierForSimulation((current) => current || firstRingId);
      setSelectedTierForSequence((current) => current || firstRingId);
      setSelectedTierForTimeline((current) => current || firstRingId);
      setSelectedTierForDebug((current) => current || firstRingId);
      setResetRingId((current) => current || firstRingId);
    } catch (error) {
      console.error('Failed to fetch RR rings:', error);
    }
  }, []);

  // Fetch all RR cursors
  const fetchRRCursors = useCallback(async () => {
    try {
      console.log('Fetching RR cursors...');
      const response = await API.fetchRRCursors();
      console.log('Raw API Response for cursors:', response);
      
      if (response?.success) {
        console.log('Cursors data received:', response.data);
        // Ensure we always have an array
        const cursorData = Array.isArray(response.data) ? response.data : [];
        setCursors(cursorData);
        setApiError(null);
        return cursorData;
      } else {
        console.warn('API returned success: false for cursors');
        setCursors([]);
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch cursors:', error);
      setApiError(error.message || 'Failed to fetch cursors');
      setCursors([]);
      notification.error({ 
        message: 'Failed to fetch cursors', 
        description: error.message,
        placement: 'topRight'
      });
      return [];
    }
  }, []);

  // Fetch RR alerts
  const fetchRRAlerts = useCallback(async () => {
    try {
      const response = await API.fetchRRAlerts({ severity: 'all' });
      if (response?.success) {
        setAlerts(response.data || []);
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Alerts fetch error:', error);
      return [];
    }
  }, []);

  // Fetch performance metrics
  const fetchPerformance = useCallback(async () => {
    try {
      const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
      const response = await API.fetchRRPerformance({ days });
      if (response?.success) {
        setPerformanceData(response.data || []);
      }
    } catch (error) {
      console.error('Performance fetch error:', error);
    }
  }, [timeRange]);

  // Fetch fairness metrics
  const fetchFairness = useCallback(async () => {
    try {
      const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
      const response = await API.fetchFairnessMetrics({ days });
      if (response?.success) {
        setFairnessData(response.data || []);
      }
    } catch (error) {
      console.error('Fairness fetch error:', error);
    }
  }, [timeRange]);

  // Fetch agent churn data
  const fetchAgentChurn = useCallback(async () => {
    try {
      const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
      const response = await API.fetchAgentChurn({ days });
      if (response?.success) {
        setChurnData(response.data);
      }
    } catch (error) {
      console.error('Churn fetch error:', error);
    }
  }, [timeRange]);

  // Fetch heatmap data
  const fetchHeatmap = useCallback(async () => {
    try {
      const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
      const response = await API.fetchRRHeatmap({ 
        days,
        ...(selectedTier !== 'ALL' && { tier: selectedTier })
      });
      if (response?.success) {
        setHeatmapData(response.data || []);
      }
    } catch (error) {
      console.error('Heatmap fetch error:', error);
    }
  }, [timeRange, selectedTier]);

  // Fetch compatibility check
  const fetchCompatibility = useCallback(async () => {
    try {
      const response = await API.checkCompatibility();
      if (response?.success) {
        setCompatibilityData(response.compatibility);
      }
    } catch (error) {
      console.error('Compatibility fetch error:', error);
    }
  }, []);

  // Fetch history for a specific tier
  const fetchHistory = useCallback(async (tier) => {
    try {
      const response = await API.fetchRRHistory(tier, { limit: historyLimit });
      if (response?.success) {
        setHistoryData(response.data);
      }
    } catch (error) {
      console.error('History fetch error:', error);
    }
  }, [historyLimit]);

  // Fetch simulation
  const fetchSimulation = useCallback(async (tier) => {
    try {
      const response = await API.simulateNextAssignment(tier, { steps: simulationSteps });
      if (response?.success) {
        setSimulationData(response.data);
      }
    } catch (error) {
      console.error('Simulation fetch error:', error);
    }
  }, [simulationSteps]);

  // Fetch agent sequence
  const fetchSequence = useCallback(async (tier) => {
    try {
      const response = await API.fetchAgentSequence(tier, { limit: 20 });
      if (response?.success) {
        setSequenceData(response.data);
      }
    } catch (error) {
      console.error('Sequence fetch error:', error);
    }
  }, []);

  // Fetch timeline
  const fetchTimeline = useCallback(async (tier) => {
    try {
      const response = await API.fetchCursorTimeline(tier, { hours: 24 });
      if (response?.success) {
        setTimelineData(response.data);
      }
    } catch (error) {
      console.error('Timeline fetch error:', error);
    }
  }, []);

  // Fetch debug cursor
  const fetchDebugCursor = useCallback(async (tier) => {
    try {
      const response = await API.debugCursor(tier);
      if (response?.success) {
        setDebugData(response.data);
      }
    } catch (error) {
      console.error('Debug fetch error:', error);
    }
  }, []);

  // Main data fetch
  const fetchData = useCallback(async (showNotification = false) => {
    setRefreshing(true);
    setApiError(null);
    
    try {
      console.log('Starting data fetch...');

      // Rings first: every ring selector below reads from them.
      await fetchRings();

      // Fetch cursors first and check result
      const cursorsResult = await fetchRRCursors();
      console.log('Cursors fetch completed:', cursorsResult);
      
      // Then fetch other data in parallel
      const otherPromises = [
        fetchRRAlerts(),
        fetchPerformance(),
        fetchFairness(),
        fetchAgentChurn(),
        fetchHeatmap(),
        fetchCompatibility()
      ];
      
      await Promise.all(otherPromises);

      // Fetch tier-specific data for selected tiers
      if (selectedTierForHistory !== 'ALL' && selectedTierForHistory) {
        await fetchHistory(selectedTierForHistory);
      }
      if (selectedTierForSimulation !== 'ALL' && selectedTierForSimulation) {
        await fetchSimulation(selectedTierForSimulation);
      }
      if (selectedTierForSequence !== 'ALL' && selectedTierForSequence) {
        await fetchSequence(selectedTierForSequence);
      }
      if (selectedTierForTimeline !== 'ALL' && selectedTierForTimeline) {
        await fetchTimeline(selectedTierForTimeline);
      }
      if (selectedTierForDebug !== 'ALL' && selectedTierForDebug) {
        await fetchDebugCursor(selectedTierForDebug);
      }

      if (showNotification) {
        notification.open({
          message: 'Dashboard Updated',
          description: 'Latest data loaded successfully',
          icon: <CheckCircleFilled style={{ color: THEME.success }} />,
          placement: 'topRight',
          duration: 2
        });
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      setApiError(error.message || 'Failed to fetch data');
      notification.error({ 
        message: 'Update Failed', 
        description: error.message || 'Please try again',
        placement: 'topRight' 
      });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [
    fetchRings,
    fetchRRCursors, fetchRRAlerts, fetchPerformance, fetchFairness,
    fetchAgentChurn, fetchHeatmap, fetchCompatibility,
    selectedTierForHistory, selectedTierForSimulation, selectedTierForSequence,
    selectedTierForTimeline, selectedTierForDebug,
    fetchHistory, fetchSimulation, fetchSequence, fetchTimeline, fetchDebugCursor
  ]);

  useEffect(() => {
    fetchData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== HANDLERS ====================
  
  const handleRefresh = () => {
    fetchData(true);
  };

  const handleResetCursor = async () => {
    try {
      const response = await API.resetRRCursor(resetRingId, {
        ringId: resetRingId
      });
      if (response?.success) {
        notification.success({
          message: 'Cursor Reset',
          description: response?.data?.message || `Successfully reset ${getRingLabel(resetRingId, ringOptions)} cursor`,
          placement: 'topRight'
        });
        setResetModalVisible(false);
        fetchData();
      }
    } catch (error) {
      notification.error({
        message: 'Reset Failed',
        description: error.message,
        placement: 'topRight'
      });
    }
  };

  const handleExportData = async () => {
    try {
      const response = await API.exportRRData({
        format: exportFormat,
        tier: exportParams.tier !== 'ALL' ? exportParams.tier : undefined,
        startDate: exportParams.startDate,
        endDate: exportParams.endDate
      });

      if (exportFormat === 'csv') {
        // Download CSV
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rr-data-${dayjs().format('YYYY-MM-DD')}.csv`;
        a.click();
      } else {
        // Download JSON
        const dataStr = JSON.stringify(response, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rr-data-${dayjs().format('YYYY-MM-DD')}.json`;
        a.click();
      }

      notification.success({
        message: 'Export Complete',
        description: 'Data exported successfully',
        placement: 'topRight'
      });
    } catch (error) {
      notification.error({
        message: 'Export Failed',
        description: error.message,
        placement: 'topRight'
      });
    }
  };

  const handleRunDiagnostic = async () => {
    try {
      const response = await API.runDiagnostic({ full: true });
      if (response?.success) {
        Modal.info({
          title: 'Diagnostic Report',
          width: 800,
          content: (
            <pre style={{ maxHeight: 400, overflow: 'auto' }}>
              {JSON.stringify(response, null, 2)}
            </pre>
          )
        });
      }
    } catch (error) {
      notification.error({ message: 'Diagnostic failed' });
    }
  };

  const testAPI = async () => {
    try {
      const response = await API.fetchRRCursors();
      console.log('Test API response:', response);
      notification.info({
        message: 'API Test',
        description: `Found ${response?.data?.length || 0} live rings`,
      });
    } catch (error) {
      console.error('Test API error:', error);
      notification.error({ 
        message: 'API Test Failed', 
        description: error.message 
      });
    }
  };

  // ==================== FILTERED DATA ====================
  
  const filteredCursors = useMemo(() => {
    console.log('Filtering cursors:', { cursors, selectedTier });
    
    if (!cursors || !Array.isArray(cursors) || cursors.length === 0) {
      console.log('No cursors to filter');
      return [];
    }
    
    if (selectedTier === 'ALL') {
      console.log('Returning all cursors:', cursors);
      return cursors;
    }
    
    const filtered = cursors.filter(c => c && c.tier === selectedTier);
    console.log('Filtered cursors:', filtered);
    return filtered;
  }, [cursors, selectedTier]);

  const unreadAlerts = alerts.filter(a => !a.read).length;

  useEffect(() => {
    if (!performanceData.length) {
      return;
    }

    const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
    const totalAssignments = performanceData.reduce((sum, ring) => sum + (ring.totalAssignments || 0), 0);
    const uniqueActiveAgents = new Set(
      cursors.flatMap((ring) => (ring.activeAgents || []).map((agent) => String(agent._id)))
    ).size;

    setMetrics({
      totalAssignments,
      activeAgents: uniqueActiveAgents,
      systemHealth: 95,
      tierCount: performanceData.length,
      throughput: Math.round(totalAssignments / days),
      responseTime: Math.round(performanceData.reduce((sum, ring) => sum + (ring.avgAssignmentTime || 0), 0) / 1000 / 60) || 150,
      uptime: 99.9
    });
  }, [performanceData, cursors, timeRange]);

  // Get all agents across all tiers with their sequence info
  const allAgentsWithSequence = useMemo(() => {
    console.log('Building agents with sequence from cursors:', cursors);
    
    const agents = [];
    
    if (!cursors || !Array.isArray(cursors)) {
      console.log('No cursors available for agent sequence');
      return [];
    }
    
    cursors.forEach(ring => {
      if (!ring || !ring.ringId) {
        console.warn('Invalid ring data:', ring);
        return;
      }

      const ringAgents = ring.activeAgents || [];
      const cursorValue = ring.cursorState?.value || 0;
      const activeCount = ring.activeAgentCount || ringAgents.length;

      console.log(`Processing ring ${ring.ringId}:`, {
        agentsCount: ringAgents.length,
        cursorValue,
        activeCount
      });

      ringAgents.forEach((agent, index) => {
        if (agent && agent._id) {
          agents.push({
            ...agent,
            tier: ring.tier,
            ringId: ring.ringId,
            ringLabel: ring.label,
            tierColor: getRingStyle(ring).primary,
            position: index,
            isCurrent: activeCount > 0 ? index === (cursorValue % activeCount) : false,
            isNext: activeCount > 0 ? index === ((cursorValue + 1) % activeCount) : false,
            waitTime: activeCount > 0 ? Math.abs(index - (cursorValue % activeCount)) * 2.5 : 0,
            totalInTier: activeCount
          });
        }
      });
    });
    
    console.log('Total agents with sequence:', agents.length);
    
    return agents.sort((a, b) => {
      if (a.ringLabel !== b.ringLabel) return a.ringLabel.localeCompare(b.ringLabel);
      return a.position - b.position;
    });
  }, [cursors]);

  // Filter agents based on selected tier
  const filteredAgents = useMemo(() => {
    if (selectedTierForRotation === 'ALL') return allAgentsWithSequence;
    return allAgentsWithSequence.filter(a => a.tier === selectedTierForRotation);
  }, [allAgentsWithSequence, selectedTierForRotation]);

  // Search filter
  const searchedAgents = useMemo(() => {
    if (!searchQuery) return filteredAgents;
    const query = searchQuery.toLowerCase();
    return filteredAgents.filter(a => 
      a.firstName?.toLowerCase().includes(query) ||
      a.lastName?.toLowerCase().includes(query) ||
      a.email?.toLowerCase().includes(query) ||
      a.tier?.toLowerCase().includes(query) ||
      a.ringLabel?.toLowerCase().includes(query)
    );
  }, [filteredAgents, searchQuery]);

  // ==================== RENDER SECTIONS ====================

  // Overview Tab
  const renderOverview = () => (
    <>
      {renderMetrics()}
      
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16} xl={18}>
          {renderTierGrid()}
          {renderAllAgentsRotation()}
        </Col>

        <Col xs={24} lg={8} xl={6}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {renderAlerts()}
            {renderLeaderboard()}
            {renderQuickActions()}
            {renderSystemStatus()}
          </Space>
        </Col>
      </Row>
    </>
  );

  // Performance Tab
  const renderPerformance = () => (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card
          title={
            <Space>
              <BarChartOutlined style={{ color: THEME.primary }} />
              <span>Performance Metrics by Tier</span>
            </Space>
          }
          extra={
            <Select value={timeRange} onChange={setTimeRange} style={{ width: 120 }}>
              <Option value="24h">Last 24h</Option>
              <Option value="7d">Last 7 days</Option>
              <Option value="30d">Last 30 days</Option>
            </Select>
          }
          style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
        >
          <Table
            dataSource={performanceData}
            loading={refreshing}
            pagination={false}
            rowKey="ringId"
          >
            <Table.Column title="Ring" render={(_, record) => (
              <Tag color={getRingStyle(record).primary}>{record.label}</Tag>
            )} />
            <Table.Column title="Total Assignments" dataIndex="totalAssignments" render={val => val?.toLocaleString() || 0} />
            <Table.Column title="Avg Response Time" dataIndex="avgAssignmentTime" render={val => `${Math.round(val || 0)}ms`} />
            <Table.Column title="Active Agents" dataIndex={["agentStats", "activeCount"]} render={val => val || 0} />
            <Table.Column 
              title="Fairness Score" 
              render={(_, record) => {
                const score = record.fairness?.fairnessScore || 0;
                return (
                  <Progress percent={Math.round(score * 100)} size="small" strokeColor={score > 0.7 ? THEME.success : THEME.warning} />
                );
              }}
            />
          </Table>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card
          title={
            <Space>
              <PieChartOutlined style={{ color: THEME.purple }} />
              <span>Assignment Methods</span>
            </Space>
          }
          style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
        >
          {performanceData.map((ring) => (
            <div key={ring.ringId} style={{ marginBottom: 20 }}>
              <Text strong>{ring.label}</Text>
              <div style={{ marginTop: 10 }}>
                {Object.entries(ring.assignmentsByMethod || {}).map(([method, data]) => (
                  <div key={method} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ width: 150 }}>
                      <Tag color="blue">{method}</Tag>
                    </div>
                    <Progress 
                      percent={Math.round((data.count / (ring.totalAssignments || 1)) * 100)} 
                      size="small"
                      format={() => data.count}
                      style={{ flex: 1, margin: 0 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card
          title={
            <Space>
              <LineChartOutlined style={{ color: THEME.success }} />
              <span>Cursor Progression</span>
            </Space>
          }
          style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
        >
          {filteredCursors.map((ring) => (
            <div key={ring.ringId} style={{ marginBottom: 20 }}>
              <Space style={{ marginBottom: 8 }}>
                <Tag color={getRingStyle(ring).primary}>{ring.label}</Tag>
                <Text type="secondary">Counter: {ring.currentCounter || 0}</Text>
              </Space>
              <Progress 
                percent={((ring.currentCounter || 0) % 100)} 
                strokeColor={getRingStyle(ring).primary}
                format={() => `Position ${(ring.currentCounter || 0) % (ring.activeAgentCount || 1)}`}
              />
            </div>
          ))}
        </Card>
      </Col>
    </Row>
  );

  // History Tab
  const renderHistory = () => {
    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <HistoryOutlined style={{ color: THEME.primary }} />
                <span>Assignment History</span>
              </Space>
            }
            extra={
              <Space>
                <Select 
                  value={selectedTierForHistory} 
                  onChange={async (val) => {
                    setSelectedTierForHistory(val);
                    await fetchHistory(val);
                  }}
                  style={{ width: 180 }}
                >
                  {ringOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
                </Select>
                <Select 
                  value={historyLimit} 
                  onChange={setHistoryLimit}
                  style={{ width: 100 }}
                >
                  <Option value={20}>20</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                </Select>
                <Button icon={<ReloadOutlined />} onClick={() => fetchHistory(selectedTierForHistory)} />
              </Space>
            }
            style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
          >
            {historyData && (
              <>
                <Space style={{ marginBottom: 16 }} wrap>
                  <Tag color={getRingStyle(historyData).primary}>{historyData.label}</Tag>
                  <Text type="secondary">RR Key: {historyData.rrKey}</Text>
                  <Text type="secondary">Cursor: {historyData.cursorState?.value || 0}</Text>
                </Space>
                <Timeline mode="left">
                  {historyData.recentAssignments?.map((assignment, idx) => (
                    <Timeline.Item
                      key={idx}
                      color={getRingStyle(historyData).primary}
                      dot={<UserOutlined />}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <Text strong>{assignment.personalInfo?.fullName}</Text>
                          <div>
                            <Tag color="blue">{assignment.assignmentMetadata?.assignmentMethod}</Tag>
                            {assignment.isExistingCustomer && <Tag color="green">Returning</Tag>}
                            {assignment.tierLaneLabel && <Tag color="purple">{assignment.tierLaneLabel}</Tag>}
                          </div>
                        </div>
                        <Text type="secondary">{dayjs(assignment.assignedDate).fromNow()}</Text>
                      </div>
                      <div>Assigned to: {assignment.agent?.name || 'Unknown'}</div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </>
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <Space>
                <OrderedListOutlined style={{ color: THEME.purple }} />
                <span>RR Sequence</span>
              </Space>
            }
            style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
          >
            <Table
              dataSource={historyData?.agentSequence || []}
              pagination={false}
              rowKey={(record) => record.agent?._id || Math.random()}
            >
              <Table.Column title="Position" dataIndex="position" render={pos => pos + 1} />
              <Table.Column 
                title="Sales-Agent"
                render={(_, record) => (
                  <Space>
                    <Avatar src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${record.agent?.email}`} />
                    <div>
                      <div>{record.agent?.name}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{record.agent?.email}</Text>
                    </div>
                  </Space>
                )}
              />
              <Table.Column 
                title="Status" 
                render={(_, record) => (
                  <>
                    {record.isCurrent && <Tag color="processing">Current</Tag>}
                    {record.isNext && <Tag color="success">Next</Tag>}
                  </>
                )}
              />
              <Table.Column title="Leads Assigned" dataIndex="leadsAssigned" />
            </Table>
          </Card>
        </Col>
      </Row>
    );
  };

  // Simulation Tab
  const renderSimulation = () => {
    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <StepForwardOutlined style={{ color: THEME.primary }} />
                <span>Assignment Simulation</span>
              </Space>
            }
            extra={
              <Space>
                <Select value={selectedTierForSimulation} onChange={async (val) => {
                  setSelectedTierForSimulation(val);
                  await fetchSimulation(val);
                }} style={{ width: 180 }}>
                  {ringOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
                </Select>
                <Input 
                  type="number" 
                  value={simulationSteps} 
                  onChange={(e) => setSimulationSteps(parseInt(e.target.value) || 5)}
                  style={{ width: 80 }}
                  min={1}
                  max={10}
                />
                <Button 
                  type="primary" 
                  icon={<PlayCircleOutlined />}
                  onClick={() => fetchSimulation(selectedTierForSimulation)}
                >
                  Simulate
                </Button>
              </Space>
            }
            style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
          >
            {simulationData && (
              <>
                <Space style={{ marginBottom: 16 }} wrap>
                  <Tag color={getRingStyle(simulationData).primary}>{simulationData.label}</Tag>
                  <Text type="secondary">RR Key: {simulationData.rrKey}</Text>
                  <Text type="secondary">Cursor: {simulationData.currentCursor?.value || 0}</Text>
                </Space>
                <Steps
                  current={0}
                  direction="horizontal"
                  style={{ marginBottom: 30 }}
                >
                  {simulationData.simulation?.map((step, idx) => (
                    <Steps.Step
                      key={idx}
                      title={`Step ${idx + 1}`}
                      description={step.agent?.name}
                      icon={<StepForwardOutlined />}
                    />
                  ))}
                </Steps>

                <Table
                  dataSource={simulationData.simulation || []}
                  pagination={false}
                  rowKey="step"
                >
                  <Table.Column title="Step" dataIndex="step" />
                  <Table.Column 
                    title="Sales-Agent"
                    render={(_, record) => (
                      <Space>
                        <Avatar src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${record.agent?.email}`} />
                        <div>
                          <div>{record.agent?.name}</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>{record.agent?.email}</Text>
                        </div>
                      </Space>
                    )}
                  />
                  <Table.Column 
                    title="Counter" 
                    render={(_, record) => (
                      <Space>
                        <Tag>{record.counterBefore} → {record.counterAfter}</Tag>
                      </Space>
                    )}
                  />
                  <Table.Column title="Position" render={(_, record) => (record.agentIndex || 0) + 1} />
                </Table>
              </>
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <Space>
                <EyeOutlined style={{ color: THEME.success }} />
                <span>Visualization</span>
              </Space>
            }
            style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
          >
            {simulationData && (
              <div style={{ display: 'flex', justifyContent: 'space-around', padding: 20, flexWrap: 'wrap', gap: 16 }}>
                {simulationData.visualization?.agents?.map((agent, idx) => (
                  <Tooltip key={idx} title={agent.agent?.name}>
                    <div style={{ textAlign: 'center' }}>
                      <Avatar
                        size={64}
                        src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.agent?.email}`}
                        style={{
                          border: `4px solid ${agent.willBeNext ? THEME.success : THEME.border}`,
                          opacity: agent.willBeNext ? 1 : 0.6
                        }}
                      />
                      <div style={{ marginTop: 8 }}>
                        <Tag color={agent.willBeNext ? 'success' : 'default'}>
                          {agent.willBeNext ? 'NEXT' : `#${idx + 1}`}
                        </Tag>
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  // Analytics Tab
  const renderAnalytics = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <Card
          title={
            <Space>
              <HeatMapOutlined style={{ color: THEME.warning }} />
              <span>Assignment Heatmap</span>
            </Space>
          }
          extra={
            <Select value={selectedTier} onChange={setSelectedTier} style={{ width: 120 }}>
              <Option value="ALL">All Tiers</Option>
              <Option value="TIER-1">Tier 1</Option>
              <Option value="TIER-2">Tier 2</Option>
              <Option value="TIER-3">Tier 3</Option>
            </Select>
          }
          style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
        >
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text type="secondary">Heatmap visualization would render here</Text>
          </div>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card
          title={
            <Space>
              <LineChartOutlined style={{ color: THEME.primary }} />
              <span>Agent Churn Analysis</span>
            </Space>
          }
          style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
        >
          {churnData && (
            <>
              <Statistic
                title="Total Agents"
                value={churnData.summary?.totalAgents || 0}
                style={{ marginBottom: 20 }}
              />
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Active"
                    value={churnData.summary?.activeAgents || 0}
                    valueStyle={{ color: THEME.success }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Inactive"
                    value={churnData.summary?.inactiveAgents || 0}
                    valueStyle={{ color: THEME.warning }}
                  />
                </Col>
              </Row>
              <Divider />
              {(churnData.ringStats || []).map((stats) => (
                <div key={stats.ringId} style={{ marginBottom: 16 }}>
                  <Text strong>{stats.label}</Text>
                  <Progress 
                    percent={Math.round((stats.activeAgents / ((stats.activeAgents + stats.inactiveAgents) || 1)) * 100) || 0}
                    strokeColor={THEME.success}
                    format={() => `${stats.activeAgents} active, ${stats.inactiveAgents} inactive`}
                  />
                </div>
              ))}
            </>
          )}
        </Card>
      </Col>

      <Col span={24}>
        <Card
          title={
            <Space>
              <BarChartOutlined style={{ color: THEME.purple }} />
              <span>Fairness Metrics</span>
            </Space>
          }
          style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
        >
          <Table
            dataSource={fairnessData}
            loading={refreshing}
            pagination={false}
            rowKey="ringId"
          >
            <Table.Column title="Ring" dataIndex="label" />
            <Table.Column 
              title="Gini Coefficient" 
              render={(_, record) => {
                const val = record.metrics?.giniCoefficient || 0;
                return (
                  <Tag color={val < 0.3 ? 'success' : val < 0.5 ? 'warning' : 'error'}>
                    {val.toFixed(3)}
                  </Tag>
                );
              }}
            />
            <Table.Column title="Std Dev" render={(_, record) => record.metrics?.stdDev?.toFixed(2) || '0'} />
            <Table.Column title="Min/Max" render={(_, record) => 
              `${record.metrics?.minAssignments || 0} / ${record.metrics?.maxAssignments || 0}`
            } />
            <Table.Column title="Fairness Score" render={(_, record) => {
              const score = record.metrics?.fairnessScore || 0;
              return (
                <Progress percent={Math.round(score * 100)} size="small" />
              );
            }} />
          </Table>
        </Card>
      </Col>
    </Row>
  );

  // Debug Tab
  const renderDebug = () => {
    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <BugOutlined style={{ color: THEME.danger }} />
                <span>System Debug</span>
              </Space>
            }
            extra={
              <Space>
                <Select value={selectedTierForDebug} onChange={async (val) => {
                  setSelectedTierForDebug(val);
                  await fetchDebugCursor(val);
                }} style={{ width: 180 }}>
                  {ringOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
                </Select>
                <Button icon={<ReloadOutlined />} onClick={() => fetchDebugCursor(selectedTierForDebug)} />
                <Button 
                  type="primary" 
                  danger 
                  icon={<CodeOutlined />}
                  onClick={handleRunDiagnostic}
                >
                  Run Diagnostic
                </Button>
              </Space>
            }
            style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
          >
            {debugData && (
              <Tabs defaultActiveKey="cursor">
                <TabPane tab="Cursor State" key="cursor">
                  <Descriptions bordered column={1}>
                    <Descriptions.Item label="Ring">{debugData.label}</Descriptions.Item>
                    <Descriptions.Item label="RR Key">{debugData.rrKey}</Descriptions.Item>
                    <Descriptions.Item label="Cursor Value">{debugData.systemCheck?.ringValue}</Descriptions.Item>
                    <Descriptions.Item label="Next Index">{debugData.systemCheck?.ringNextIndex}</Descriptions.Item>
                    <Descriptions.Item label="Agent Count">{debugData.systemCheck?.ringAgentCount}</Descriptions.Item>
                    <Descriptions.Item label="Formula">{debugData.systemCheck?.formula}</Descriptions.Item>
                    <Descriptions.Item label="Compatible">
                      <Tag color={debugData.systemCheck?.isCompatible ? 'success' : 'error'}>
                        {debugData.systemCheck?.isCompatible ? 'Yes' : 'No'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </TabPane>
                
                <TabPane tab="Agents" key="agents">
                  <Table
                    dataSource={debugData.activeAgents?.agents || []}
                    pagination={false}
                    rowKey="email"
                    size="small"
                  >
                    <Table.Column title="Name" render={(_, record) => record.name} />
                    <Table.Column title="Email" dataIndex="email" />
                    <Table.Column title="Mailbox" dataIndex="mailboxId" />
                  </Table>
                </TabPane>

                <TabPane tab="Recent Assignments" key="assignments">
                  <Timeline>
                    {debugData.recentAssignments?.map((item, idx) => (
                      <Timeline.Item key={idx} color="blue">
                        <Space direction="vertical" size={0}>
                          <Text strong>{item.agent?.name}</Text>
                          {item.tierLaneLabel && <Tag color="purple">{item.tierLaneLabel}</Tag>}
                          <Text type="secondary">{item.assignmentMethod} - Counter: {item.assignmentCounter}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(item.assignedDate).fromNow()}</Text>
                        </Space>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </TabPane>
              </Tabs>
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <Space>
                <SafetyCertificateOutlined style={{ color: THEME.primary }} />
                <span>Compatibility Check</span>
              </Space>
            }
            style={{ borderRadius: 20, border: 'none', boxShadow: THEME.shadow }}
          >
            {compatibilityData && (
              <>
                <Alert
                  type={compatibilityData.summary?.isCompatible ? 'success' : 'warning'}
                  message={compatibilityData.summary?.isCompatible ? 'System Compatible' : 'Compatibility Issues Found'}
                  description={compatibilityData.summary?.recommendations}
                  showIcon
                  style={{ marginBottom: 20 }}
                />
                
                <Table
                  dataSource={compatibilityData.checks || []}
                  pagination={false}
                  rowKey="check"
                >
                  <Table.Column title="Check" dataIndex="check" />
                  <Table.Column 
                    title="Status" 
                    render={(_, record) => (
                      <Tag color={record.passed ? 'success' : 'error'}>
                        {record.passed ? '✓ Passed' : '✗ Failed'}
                      </Tag>
                    )}
                  />
                  <Table.Column title="Details" dataIndex="details" />
                </Table>
              </>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  // Render Metrics
  const renderMetrics = () => {
    if (!metrics) return null;

    const metricsData = [
      {
        title: 'Total Assignments',
        value: metrics.totalAssignments.toLocaleString(),
        change: '+12.5%',
        icon: <HistoryOutlined />,
        color: THEME.primary,
        bg: THEME.primaryLight + '20'
      },
      {
        title: 'Active Agents',
        value: metrics.activeAgents,
        change: '+5.2%',
        icon: <TeamOutlined />,
        color: THEME.success,
        bg: THEME.successLight
      },
      {
        title: 'System Health',
        value: metrics.systemHealth + '%',
        change: metrics.systemHealth > 80 ? 'Good' : 'Attention',
        icon: <SafetyCertificateOutlined />,
        color: metrics.systemHealth > 80 ? THEME.success : THEME.warning,
        bg: metrics.systemHealth > 80 ? THEME.successLight : THEME.warningLight
      },
      {
        title: 'Response Time',
        value: metrics.responseTime + 'ms',
        change: '-8.3%',
        icon: <ClockCircleOutlined />,
        color: THEME.purple,
        bg: THEME.purpleLight
      }
    ];

    return (
      <div style={{ padding: '24px 24px 0' }}>
        <Row gutter={[16, 16]}>
          {metricsData.map((metric, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <div
                className="metric-card"
                onMouseEnter={() => setHoveredCard(`metric-${index}`)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'white',
                  borderRadius: 20,
                  padding: 20,
                  boxShadow: hoveredCard === `metric-${index}` ? THEME.shadowHover : THEME.shadow,
                  transform: hoveredCard === `metric-${index}` ? 'translateY(-4px)' : 'none',
                  transition: THEME.transition,
                  border: `1px solid ${THEME.border}`,
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Text type="secondary" style={{ fontSize: 14, fontWeight: 500 }}>
                    {metric.title}
                  </Text>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: metric.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: metric.color,
                    fontSize: 20
                  }}>
                    {metric.icon}
                  </div>
                </div>
                
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 32, fontWeight: 700, color: metric.color, lineHeight: 1 }}>
                    {metric.value}
                  </span>
                  <Tag
                    color={metric.change.startsWith('+') ? 'success' : 'warning'}
                    style={{
                      borderRadius: 40,
                      padding: '2px 8px',
                      fontSize: 12,
                      margin: 0
                    }}
                  >
                    {metric.change.startsWith('+') ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {metric.change}
                  </Tag>
                </div>

                <div style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: `1px solid ${THEME.border}`,
                  fontSize: 12,
                  color: THEME.textLight
                }}>
                  vs previous period
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Render Tier Grid - FIXED VERSION
  const renderTierGrid = () => {
    console.log('Rendering tier grid with:', {
      filteredCursors,
      length: filteredCursors?.length,
      cursorsLength: cursors?.length,
      selectedTier
    });

    // Show error state if API failed
    if (apiError) {
      return (
        <Card
          title={
            <Space>
              <GatewayOutlined style={{ color: THEME.primary }} />
              <span>Active Tiers</span>
            </Space>
          }
          extra={
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={refreshing}
            >
              Retry
            </Button>
          }
          style={{
            borderRadius: 20,
            border: 'none',
            boxShadow: THEME.shadow,
            marginBottom: 24
          }}
        >
          <Alert
            message="Failed to Load Tiers"
            description={apiError}
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={handleRefresh}>
                Retry
              </Button>
            }
          />
        </Card>
      );
    }

    // Show loading state
    if (refreshing && !filteredCursors.length) {
      return (
        <Card
          title={
            <Space>
              <GatewayOutlined style={{ color: THEME.primary }} />
              <span>Active Tiers</span>
            </Space>
          }
          style={{
            borderRadius: 20,
            border: 'none',
            boxShadow: THEME.shadow,
            marginBottom: 24
          }}
        >
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Loading tier data...</Text>
            </div>
          </div>
        </Card>
      );
    }

    // Show empty state with debug info
    if (!filteredCursors || filteredCursors.length === 0) {
      return (
        <Card
          title={
            <Space>
              <GatewayOutlined style={{ color: THEME.primary }} />
              <span>Active Tiers</span>
            </Space>
          }
          extra={
            <Space>
              <Text type="secondary">
                {cursors?.length ? 'No tiers match filter' : 'No tier data available'}
              </Text>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                loading={refreshing}
                size="small"
              >
                Refresh
              </Button>
            </Space>
          }
          style={{
            borderRadius: 20,
            border: 'none',
            boxShadow: THEME.shadow,
            marginBottom: 24
          }}
        >
          {cursors?.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>No tier data available</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Try refreshing or check API connection
                  </Text>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    loading={refreshing}
                    style={{ marginTop: 16 }}
                  >
                    Refresh Data
                  </Button>
                </Space>
              }
              style={{ padding: '48px 24px' }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>No tiers match filter: {selectedTier}</Text>
                  <Button 
                    onClick={() => setSelectedTier('ALL')}
                    size="small"
                  >
                    Show All Tiers
                  </Button>
                </Space>
              }
              style={{ padding: '48px 24px' }}
            />
          )}
        </Card>
      );
    }

    // Show actual tier data
    return (
      <Card
        title={
          <Space>
            <GatewayOutlined style={{ color: THEME.primary }} />
            <span>Active Tiers</span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">{filteredCursors.length} Rings Active</Text>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={refreshing}
              size="small"
            />
          </Space>
        }
        style={{
          borderRadius: 20,
          border: 'none',
          boxShadow: THEME.shadow,
          marginBottom: 24
        }}
      >
        <Row gutter={[20, 20]}>
          {filteredCursors.map((ring) => {
            if (!ring || !ring.ringId) {
              console.warn('Invalid ring data:', ring);
              return null;
            }

            const style = getRingStyle(ring);
            const cursorValue = ring.currentCounter || 0;
            const activeCount = ring.activeAgentCount || 0;
            const currentAgent = ring.currentAgent;
            const nextAgent = ring.nextAgent;
            const isHovered = hoveredCard === ring.ringId;

            return (
              <Col xs={24} md={12} lg={8} key={ring.ringId}>
                <div
                  className="tier-card"
                  onMouseEnter={() => setHoveredCard(ring.ringId)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    background: 'white',
                    borderRadius: 20,
                    overflow: 'hidden',
                    border: `1px solid ${isHovered ? style.primary : THEME.border}`,
                    boxShadow: isHovered ? `0 10px 30px -10px ${style.primary}` : 'none',
                    transform: isHovered ? 'translateY(-2px)' : 'none',
                    transition: THEME.transition,
                    marginBottom: 16
                  }}
                >
                  {/* Header */}
                  <div style={{
                    background: style.gradient,
                    padding: 16,
                    position: 'relative'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      color: 'white'
                    }}>
                      <Space>
                        {style.icon}
                        <span style={{ fontWeight: 600 }}>{ring.label}</span>
                      </Space>
                      <Badge
                        count={activeCount}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{ padding: 16 }}>
                    {/* Next Agent Preview */}
                    {nextAgent && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        background: style.light,
                        borderRadius: 12,
                        padding: 12,
                        marginBottom: 12
                      }}>
                        <Avatar
                          size={40}
                          src={nextAgent && `https://api.dicebear.com/7.x/avataaars/svg?seed=${nextAgent?.email}`}
                          style={{ border: `2px solid ${style.primary}` }}
                        >
                          {nextAgent && getInitials(nextAgent.firstName || nextAgent.name)}
                        </Avatar>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11 }}>NEXT UP</Text>
                          <div style={{ fontWeight: 600 }}>
                            {nextAgent?.firstName || 'No agent'} {nextAgent?.lastName || ''}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Current Agent */}
                    {currentAgent && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '8px 0'
                      }}>
                        <Avatar
                          size={40}
                          src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${currentAgent.email}`}
                        />
                        <div style={{ flex: 1 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>CURRENT</Text>
                          <div style={{ fontWeight: 600 }}>
                            {currentAgent.firstName} {currentAgent.lastName}
                          </div>
                        </div>
                        <Badge status={currentAgent.isActive ? "success" : "default"} />
                      </div>
                    )}

                    {/* Counter */}
                    <div style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: `1px solid ${THEME.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Text type="secondary">Counter</Text>
                      <Tag color={style.primary} style={{ borderRadius: 40 }}>
                        {cursorValue}
                      </Tag>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  // Render All Agents Rotation
  const renderAllAgentsRotation = () => {
    if (!searchedAgents.length) {
      return (
        <Card
          title={
            <Space>
              <OrderedListOutlined style={{ color: THEME.primary }} />
              <span>All Agents in Rotation</span>
            </Space>
          }
          style={{
            borderRadius: 20,
            border: 'none',
            boxShadow: THEME.shadow
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No agents in rotation"
          />
        </Card>
      );
    }

    // Group agents by tier
    const agentsByTier = {};
    searchedAgents.forEach(agent => {
      const ringKey = agent.ringLabel;
      if (!agentsByTier[ringKey]) {
        agentsByTier[ringKey] = [];
      }
      agentsByTier[ringKey].push(agent);
    });

    return (
      <Card
        title={
          <Space>
            <OrderedListOutlined style={{ color: THEME.primary }} />
            <span>All Agents in Rotation</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              value={selectedTierForRotation}
              onChange={setSelectedTierForRotation}
              size="small"
              style={{ width: 120 }}
              dropdownStyle={{ borderRadius: 12 }}
            >
              <Option value="ALL">All Tiers</Option>
              <Option value="TIER-1">Tier 1</Option>
              <Option value="TIER-2">Tier 2</Option>
              <Option value="TIER-3">Tier 3</Option>
            </Select>
            <Button
              size="small"
              icon={rotationView === 'grid' ? <BarChartOutlined /> : <PartitionOutlined />}
              onClick={() => setRotationView(rotationView === 'grid' ? 'timeline' : 'grid')}
              style={{ borderRadius: 40 }}
            >
              {rotationView === 'grid' ? 'Timeline' : 'Grid'}
            </Button>
          </Space>
        }
        style={{
          borderRadius: 20,
          border: 'none',
          boxShadow: THEME.shadow
        }}
      >
        {rotationView === 'grid' ? (
          // Grid View
          <div>
            {Object.entries(agentsByTier).map(([ringLabel, agents]) => {
              const tierStyle = getRingStyle(agents[0]);
              return (
              <div key={ringLabel} style={{ marginBottom: 24 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: tierStyle.light || THEME.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tierStyle.primary || THEME.primary
                  }}>
                    {tierStyle.icon || <UserOutlined />}
                  </div>
                  <Title level={5} style={{ margin: 0 }}>{ringLabel}</Title>
                  <Badge count={agents.length} style={{ background: tierStyle.primary || THEME.primary }} />
                </div>

                <Row gutter={[12, 12]}>
                  {agents.map((agent, idx) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={`${agent.tier}-${agent.position}-${idx}`}>
                      <div
                        onClick={() => {
                          setActiveAgent(agent);
                          setDrawerType('agent');
                          setDrawerVisible(true);
                        }}
                        style={{
                          background: agent.isCurrent ? getRingStyle(agent).light : 'white',
                          borderRadius: 16,
                          padding: 16,
                          border: `2px solid ${
                            agent.isCurrent ? getRingStyle(agent).primary :
                            agent.isNext ? THEME.success :
                            THEME.border
                          }`,
                          cursor: 'pointer',
                          transition: THEME.transition,
                          position: 'relative',
                          ':hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: THEME.shadowHover
                          }
                        }}
                      >
                        {/* Status Badge */}
                        {agent.isCurrent && (
                          <div style={{
                            position: 'absolute',
                            top: -8,
                            left: 12,
                            background: getRingStyle(agent).primary,
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: 40,
                            fontSize: 10,
                            fontWeight: 600
                          }}>
                            CURRENT
                          </div>
                        )}
                        
                        {agent.isNext && !agent.isCurrent && (
                          <div style={{
                            position: 'absolute',
                            top: -8,
                            left: 12,
                            background: THEME.success,
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: 40,
                            fontSize: 10,
                            fontWeight: 600
                          }}>
                            NEXT
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Avatar
                            size={48}
                            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.email}`}
                            style={{
                              border: `2px solid ${
                                agent.isCurrent ? getRingStyle(agent).primary :
                                agent.isNext ? THEME.success :
                                THEME.border
                              }`
                            }}
                          />
                          
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                              {agent.firstName} {agent.lastName}
                            </div>
                            <Space size={4} wrap>
                              <Tag 
                                color={getRingStyle(agent).primary} 
                                style={{ 
                                  borderRadius: 40, 
                                  fontSize: 10,
                                  margin: 0
                                }}
                              >
                                {agent.ringLabel}
                              </Tag>
                              <Tag 
                                color={agent.isActive ? 'success' : 'default'} 
                                style={{ borderRadius: 40, fontSize: 10, margin: 0 }}
                              >
                                {agent.isActive ? 'Active' : 'Inactive'}
                              </Tag>
                            </Space>
                          </div>
                        </div>

                        {/* Position Info */}
                        <div style={{
                          marginTop: 12,
                          paddingTop: 12,
                          borderTop: `1px solid ${THEME.border}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Position #{agent.position + 1} of {agent.totalInTier}
                          </Text>
                          {agent.waitTime > 0 && (
                            <Badge 
                              count={`${Math.round(agent.waitTime)}min`}
                              style={{ 
                                background: THEME.cardBgAlt,
                                color: THEME.textSecondary,
                                fontSize: 10
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )})}
          </div>
        ) : (
          // Timeline View
          <div style={{ padding: '20px 0' }}>
            <Steps
              direction="vertical"
              current={0}
              size="small"
            >
              {searchedAgents.map((agent, idx) => (
                <Steps.Step
                  key={idx}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <Avatar
                        size={40}
                        src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.email}`}
                        style={{
                          border: `2px solid ${
                            agent.isCurrent ? getRingStyle(agent).primary :
                            agent.isNext ? THEME.success :
                            THEME.border
                          }`
                        }}
                      />
                      <div>
                        <Space>
                          <Text strong>{agent.firstName} {agent.lastName}</Text>
                          <Tag color={getRingStyle(agent).primary} style={{ borderRadius: 40 }}>
                            {agent.ringLabel}
                          </Tag>
                        </Space>
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {agent.isCurrent ? 'Currently serving' : 
                             agent.isNext ? 'Next in line' : 
                             `Position #${agent.position + 1} · Est. wait ${Math.round(agent.waitTime)}min`}
                          </Text>
                        </div>
                      </div>
                    </div>
                  }
                  status={agent.isCurrent ? 'process' : agent.isNext ? 'wait' : 'wait'}
                  icon={agent.isCurrent ? <PlayCircleOutlined /> : undefined}
                />
              ))}
            </Steps>
          </div>
        )}
      </Card>
    );
  };

  // Render Alerts
  const renderAlerts = () => {
    const activeAlerts = alerts.filter(a => !a.read).slice(0, 3);

    return (
      <Card
        title={
          <Space>
            <BellOutlined />
            <span>Active Alerts</span>
          </Space>
        }
        extra={<Badge count={unreadAlerts} />}
        style={{
          borderRadius: 20,
          border: 'none',
          boxShadow: THEME.shadow
        }}
        bodyStyle={{ padding: 0 }}
      >
        {activeAlerts.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <CheckCircleFilled style={{ fontSize: 48, color: THEME.success, marginBottom: 16 }} />
            <Text>All systems operational</Text>
          </div>
        ) : (
          <List
            dataSource={activeAlerts}
            renderItem={(alert) => {
              const severity = alert.severity === 'CRITICAL' ? {
                color: THEME.danger,
                bg: THEME.dangerLight,
                icon: <ExclamationCircleFilled />
              } : alert.severity === 'HIGH' ? {
                color: THEME.warning,
                bg: THEME.warningLight,
                icon: <ExclamationCircleOutlined />
              } : {
                color: THEME.primary,
                bg: THEME.primaryLight,
                icon: <InfoCircleOutlined />
              };

              return (
                <List.Item
                  style={{
                    padding: '16px 20px',
                    borderLeft: `3px solid ${severity.color}`,
                    background: severity.bg + '40',
                    cursor: 'pointer',
                    transition: THEME.transition,
                    ':hover': { background: severity.bg }
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={severity.icon}
                        style={{ background: severity.color, color: 'white' }}
                      />
                    }
                    title={
                      <Space>
                        <Text strong style={{ color: severity.color }}>{alert.severity}</Text>
                        <Tag color={getRingStyle(alert).primary} style={{ borderRadius: 40 }}>
                          {alert.label || alert.tier}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>{alert.message}</div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {dayjs(alert.timestamp).fromNow()}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    );
  };

  // Render Leaderboard
  const renderLeaderboard = () => {
    const topAgents = allAgentsWithSequence
      .map(agent => ({
        ...agent,
        assignments: Math.floor(Math.random() * 500) + 100 // Placeholder
      }))
      .sort((a, b) => b.assignments - a.assignments)
      .slice(0, 5);

    return (
      <Card
        title={
          <Space>
            <TrophyOutlined style={{ color: '#f59e0b' }} />
            <span>Top Performers</span>
          </Space>
        }
        style={{
          borderRadius: 20,
          border: 'none',
          boxShadow: THEME.shadow
        }}
      >
        <List
          dataSource={topAgents}
          renderItem={(agent, index) => (
            <List.Item
              style={{
                padding: '12px 0',
                cursor: 'pointer',
                transition: THEME.transition,
                ':hover': { transform: 'translateX(4px)' }
              }}
              onClick={() => {
                setActiveAgent(agent);
                setDrawerType('agent');
                setDrawerVisible(true);
              }}
            >
              <List.Item.Meta
                avatar={
                  <div style={{ position: 'relative' }}>
                    <Avatar
                      size={48}
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.email}`}
                      style={{ border: `2px solid ${agent.tierColor}` }}
                    />
                    {index === 0 && (
                      <CrownFilled style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        color: '#f59e0b',
                        fontSize: 16
                      }} />
                    )}
                  </div>
                }
                title={
                  <Space>
                    <Text strong>{agent.firstName} {agent.lastName}</Text>
                    <Tag color={agent.tierColor} style={{ borderRadius: 40 }}>{agent.ringLabel}</Tag>
                  </Space>
                }
                description={`${agent.assignments.toLocaleString()} tasks completed`}
              />
            </List.Item>
          )}
        />
      </Card>
    );
  };

  // Render Quick Actions
  const renderQuickActions = () => (
    <Card
      title="Quick Actions"
      style={{
        borderRadius: 20,
        border: 'none',
        boxShadow: THEME.shadow
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button
          type="primary"
          block
          icon={<PlayCircleOutlined />}
          onClick={handleRunDiagnostic}
          style={{
            background: THEME.gradient,
            border: 'none',
            borderRadius: 40,
            height: 44
          }}
        >
          Run Analysis
        </Button>
        <Button
          block
          icon={<DownloadOutlined />}
          onClick={() => {
            setDrawerType('export');
            setDrawerVisible(true);
          }}
          style={{ borderRadius: 40, height: 44 }}
        >
          Export Data
        </Button>
        <Button
          block
          icon={<SyncOutlined />}
          danger
          onClick={() => setResetModalVisible(true)}
          style={{ borderRadius: 40, height: 44 }}
        >
          Reset Cursors
        </Button>
        <Button
          block
          icon={<ExperimentOutlined />}
          onClick={() => {
            setActiveTab('simulation');
            window.scrollTo({ top: 0 });
          }}
          style={{ borderRadius: 40, height: 44 }}
        >
          Run Simulation
        </Button>
        <Button
          block
          icon={<BugOutlined />}
          onClick={testAPI}
          style={{ borderRadius: 40, height: 44 }}
        >
          Test API
        </Button>
      </Space>
    </Card>
  );

  // Render System Status
  const renderSystemStatus = () => (
    <Card
      title="System Status"
      style={{
        borderRadius: 20,
        border: 'none',
        boxShadow: THEME.shadow
      }}
    >
      <Statistic
        title="Uptime"
        value={metrics?.uptime || '99.9%'}
        valueStyle={{ color: THEME.success }}
        prefix={<SafetyCertificateOutlined />}
      />
      <Divider style={{ margin: '16px 0' }} />
      <Statistic
        title="Avg Response"
        value={metrics?.responseTime + 'ms' || '245ms'}
        valueStyle={{ color: THEME.primary }}
        prefix={<ClockCircleOutlined />}
      />
      <Divider style={{ margin: '16px 0' }} />
      <Statistic
        title="Throughput"
        value={metrics?.throughput || '1.2k'}
        suffix="/day"
        valueStyle={{ color: THEME.purple }}
        prefix={<RiseOutlined />}
      />
    </Card>
  );

  // Render Reset Modal
  const renderResetModal = () => (
    <Modal
      title="Reset Round Robin Cursor"
      open={resetModalVisible}
      onOk={handleResetCursor}
      onCancel={() => setResetModalVisible(false)}
      okButtonProps={{ danger: true }}
    >
      <p>This will reset the cursor counter to 0. This action cannot be undone.</p>
      <Select value={resetRingId} onChange={setResetRingId} style={{ width: '100%' }}>
        {ringOptions.map((option) => (
          <Option key={option.value} value={option.value}>{option.label}</Option>
        ))}
      </Select>
    </Modal>
  );

  // Render Drawer
  const renderDrawer = () => {
    const titles = {
      notifications: 'Notifications',
      agent: 'Agent Profile',
      export: 'Export Data'
    };

    return (
      <Drawer
        title={titles[drawerType]}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={isMobile ? '100%' : 400}
        style={{ borderRadius: isMobile ? 0 : '24px 0 0 24px' }}
        headerStyle={{ borderBottom: `1px solid ${THEME.border}`, padding: '20px 24px' }}
        bodyStyle={{ padding: 24 }}
      >
        {drawerType === 'notifications' && (
          <List
            dataSource={alerts}
            renderItem={(alert) => {
              const severity = alert.severity === 'CRITICAL' ? {
                color: THEME.danger,
                bg: THEME.dangerLight,
                icon: <ExclamationCircleFilled />
              } : alert.severity === 'HIGH' ? {
                color: THEME.warning,
                bg: THEME.warningLight,
                icon: <ExclamationCircleOutlined />
              } : {
                color: THEME.primary,
                bg: THEME.primaryLight,
                icon: <InfoCircleOutlined />
              };

              return (
                <List.Item style={{ padding: '16px 0' }}>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={severity.icon}
                        style={{ background: severity.color, color: 'white' }}
                      />
                    }
                    title={
                      <Space>
                        <Text strong style={{ color: severity.color }}>{alert.severity}</Text>
                        <Tag color={getRingStyle(alert).primary} style={{ borderRadius: 40 }}>
                          {alert.label || alert.tier}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>{alert.message}</div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {dayjs(alert.timestamp).format('MM-DD-YYYY HH:mm')}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}

        {drawerType === 'agent' && activeAgent && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <Avatar
                size={120}
                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${activeAgent.email}`}
                style={{
                  border: `4px solid ${activeAgent.tierColor || THEME.primary}`,
                  marginBottom: 16
                }}
              />
              <Title level={4} style={{ margin: '8px 0 4px' }}>
                {activeAgent.firstName} {activeAgent.lastName}
              </Title>
              <Text type="secondary">{activeAgent.email}</Text>
            </div>

            <Card
              style={{
                borderRadius: 16,
                border: `1px solid ${THEME.border}`,
                boxShadow: 'none'
              }}
            >
              <Descriptions column={1} labelStyle={{ fontWeight: 600 }}>
                <Descriptions.Item label="Status">
                  <Badge
                    status={activeAgent.isActive ? "success" : "default"}
                    text={activeAgent.isActive ? "Active" : "Inactive"}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Tier">
                  <Tag color={activeAgent.tierColor} style={{ borderRadius: 40 }}>
                    {activeAgent.ringLabel} - {getRingStyle(activeAgent)?.badge || 'Agent'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Position">
                  #{activeAgent.position + 1} of {activeAgent.totalInTier}
                </Descriptions.Item>
                <Descriptions.Item label="Est. Wait Time">
                  {Math.round(activeAgent.waitTime)} minutes
                </Descriptions.Item>
                <Descriptions.Item label="Assignments">
                  <Text strong style={{ fontSize: 18, color: THEME.primary }}>
                    {Math.floor(Math.random() * 500).toLocaleString()}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Efficiency">
                  <Progress
                    percent={Math.floor(Math.random() * 30 + 70)}
                    size="small"
                    strokeColor={THEME.success}
                    style={{ margin: 0 }}
                  />
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Quick Actions */}
            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <Button block icon={<PlayCircleOutlined />}>Assign Task</Button>
              <Button block icon={<HistoryOutlined />}>View History</Button>
            </div>
          </div>
        )}

        {drawerType === 'export' && (
          <div>
            <Select value={exportFormat} onChange={setExportFormat} style={{ width: '100%', marginBottom: 16 }}>
              <Option value="json">JSON</Option>
              <Option value="csv">CSV</Option>
            </Select>
            <Select 
              value={exportParams.tier} 
              onChange={(val) => setExportParams({...exportParams, tier: val})}
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Option value="ALL">All Tiers</Option>
              <Option value="TIER-1">Tier 1</Option>
              <Option value="TIER-2">Tier 2</Option>
              <Option value="TIER-3">Tier 3</Option>
            </Select>
            <Input 
              type="date" 
              placeholder="Start Date"
              onChange={(e) => setExportParams({...exportParams, startDate: e.target.value})}
              style={{ marginBottom: 16 }}
            />
            <Input 
              type="date" 
              placeholder="End Date"
              onChange={(e) => setExportParams({...exportParams, endDate: e.target.value})}
              style={{ marginBottom: 24 }}
            />
            <Button 
              type="primary" 
              block
              icon={<DownloadOutlined />}
              onClick={handleExportData}
              style={{ height: 44, borderRadius: 40 }}
            >
              Export Data
            </Button>
          </div>
        )}
      </Drawer>
    );
  };

  // Render Header
  const renderHeader = () => (
    <div className="dashboard-header" style={{
      background: 'rgba(255,255,255,0.8)',
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${THEME.border}`,
      padding: '16px 24px',
      transition: THEME.transition,
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              background: THEME.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 24,
              transform: 'rotate(0deg)',
              transition: 'transform 0.5s ease',
              ':hover': { transform: 'rotate(180deg)' }
            }}>
              <ThunderboltOutlined />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
                Round Robin
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Distribution Dashboard
              </Text>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12} md={10}>
          <Space size="middle" style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Select
              value={selectedTier}
              onChange={setSelectedTier}
              style={{ width: 140 }}
              dropdownStyle={{ borderRadius: 12 }}
              suffixIcon={<GatewayOutlined />}
            >
              <Option value="ALL">All Tiers</Option>
              <Option value="TIER-1">Tier 1 - Premium</Option>
              <Option value="TIER-2">Tier 2 - Business</Option>
              <Option value="TIER-3">Tier 3 - Standard</Option>
            </Select>

            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: 120 }}
              dropdownStyle={{ borderRadius: 12 }}
              suffixIcon={<CalendarOutlined />}
            >
              <Option value="24h">Last 24h</Option>
              <Option value="7d">Last 7 days</Option>
              <Option value="30d">Last 30 days</Option>
            </Select>
          </Space>
        </Col>

        <Col xs={24} md={6}>
          <Space size="middle" style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <Button 
                icon={<ReloadOutlined spin={refreshing} />} 
                onClick={handleRefresh}
                shape="circle"
                style={{ border: 'none', boxShadow: THEME.shadow }}
              />
            </Tooltip>

            <Tooltip title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <Button
                icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setFullscreen(!fullscreen)}
                shape="circle"
                style={{ border: 'none', boxShadow: THEME.shadow }}
              />
            </Tooltip>

            <Badge count={unreadAlerts} offset={[-5, 5]}>
              <Button
                icon={<BellOutlined />}
                shape="circle"
                onClick={() => {
                  setDrawerType('notifications');
                  setDrawerVisible(true);
                }}
                style={{ border: 'none', boxShadow: THEME.shadow }}
              />
            </Badge>

            <Button
              icon={<SettingOutlined />}
              shape="circle"
              style={{ border: 'none', boxShadow: THEME.shadow }}
            />
          </Space>
        </Col>
      </Row>

      <div style={{ marginTop: 16 }}>
        <Input
          placeholder="Search agents, tiers, alerts..."
          prefix={<SearchOutlined style={{ color: THEME.textLight }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{
            borderRadius: 40,
            border: 'none',
            boxShadow: THEME.shadow,
            background: 'white',
            height: 44
          }}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: THEME.background
      }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: THEME.background,
      transition: THEME.transition
    }}>
      {renderHeader()}
      
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '0 24px 48px' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: 24 }}>
          <TabPane tab="Overview" key="overview">
            {renderOverview()}
          </TabPane>
          <TabPane tab="Performance" key="performance">
            {renderPerformance()}
          </TabPane>
          <TabPane tab="History" key="history">
            {renderHistory()}
          </TabPane>
          <TabPane tab="Simulation" key="simulation">
            {renderSimulation()}
          </TabPane>
          <TabPane tab="Analytics" key="analytics">
            {renderAnalytics()}
          </TabPane>
          <TabPane tab="Debug" key="debug">
            {renderDebug()}
          </TabPane>
        </Tabs>
      </div>

      <FloatButton.Group
        trigger="click"
        icon={<MoreOutlined />}
        style={{ right: 24, bottom: 24 }}
      >
        <FloatButton icon={<ReloadOutlined spin={refreshing} />} onClick={handleRefresh} />
        <FloatButton icon={<DownloadOutlined />} onClick={() => {
          setDrawerType('export');
          setDrawerVisible(true);
        }} />
        <FloatButton icon={<ExperimentOutlined />} onClick={() => setActiveTab('simulation')} />
        <FloatButton.BackTop visibilityHeight={100} />
      </FloatButton.Group>

      {renderDrawer()}
      {renderResetModal()}
    </div>
  );
};

export default RRVisualizationDashboard;
